import React from 'react';
import { Box, Button, Flex, SimpleGrid, Text, Tooltip } from '@chakra-ui/react';
import { Coins, Info, MagnifyingGlass, NotePencil, Warning } from 'phosphor-react';
import type { ICompany } from '../../../../hooks/useCompanies';
import { GlassCard } from '../../../../components/glass/GlassCard';
import ViewNotaModal from '../../../../components/viewNota';
import ViewApuracaoModal from '../../../../components/viewApurate';
import { formatCNPJ } from '../../../../utils/FormatFunctions';
import { ImpostoCard } from './ImpostoCard';
import {
  IConfigRegime,
  formatarEmReais,
  isEmpresaFilial,
  motivosDivergencia,
  textoReceitaFinanceira,
  textoReceitaFinanceiraFora,
} from './regras';

const Metrica = ({
  rotulo,
  valor,
  destaque,
  dica,
}: {
  rotulo: string;
  valor: string;
  destaque?: boolean;
  dica?: string;
}) => (
  <Box minW={0}>
    <Text
      fontSize="10.5px"
      color="#94A3B8"
      fontWeight={600}
      textTransform="uppercase"
      letterSpacing="0.06em"
    >
      {rotulo}
    </Text>
    <Tooltip label={dica} isDisabled={!dica} hasArrow placement="top" maxW="280px" fontSize="xs">
      <Text
        fontSize={destaque ? '16px' : '13px'}
        fontWeight={destaque ? 800 : 600}
        color={destaque ? '#0F172A' : '#334155'}
        noOfLines={1}
        cursor={dica ? 'help' : undefined}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {valor}
      </Text>
    </Tooltip>
  </Box>
);

export const EmpresaCard = ({
  item,
  cfg,
  dateStart,
  dateEnd,
  onQuota,
  quotaPendente,
}: {
  item: ICompany;
  cfg: IConfigRegime;
  dateStart?: string | null;
  dateEnd?: string | null;
  onQuota: () => void;
  quotaPendente: boolean;
}) => {
  const calc = item.impostos_calculo as any;
  const rf = item.receitas_financeiras;
  const filial = isEmpresaFilial(item.cnpj);
  const motivos = motivosDivergencia(item);

  return (
    <GlassCard intensity="strong" p={{ base: '14px', md: '16px 18px' }} destacado={false}>
      {/* Identificação */}
      <Flex align="flex-start" gap={3} wrap="wrap">
        <Box minW={0} flex={1}>
          <Flex align="center" gap={2} mb={1} wrap="wrap">
            <Text
              fontSize="10.5px"
              fontWeight={700}
              color="#64748B"
              bg="rgba(15,23,42,0.05)"
              px="7px"
              py="2px"
              borderRadius="6px"
            >
              ID {item.external_id}
            </Text>
            <Text fontSize="11.5px" color="#64748B" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatCNPJ(item.cnpj)}
            </Text>
            {filial && (
              <Text
                fontSize="10.5px"
                fontWeight={700}
                color="#7C3AED"
                bg="#F5F3FF"
                px="7px"
                py="2px"
                borderRadius="6px"
              >
                Filial
              </Text>
            )}
            {item.paga_por_quotas && (
              <Text
                fontSize="10.5px"
                fontWeight={700}
                color="#7C3AED"
                bg="#EDE9FE"
                px="7px"
                py="2px"
                borderRadius="6px"
              >
                Quota
              </Text>
            )}
          </Flex>
          <Text
            fontSize="15px"
            fontWeight={700}
            color="#0F172A"
            noOfLines={1}
            title={item.razao_social}
          >
            {item.razao_social}
          </Text>
        </Box>
        <Flex align="center" gap={2} flex="none">
          {item.qt_apuracoes > 0 ? (
            <ViewApuracaoModal
              id={item.id}
              dateStart={dateStart || undefined}
              dateEnd={dateEnd || undefined}
            >
              <Button size="xs" variant="ghost" colorScheme="blue" borderRadius="8px">
                {item.qt_apuracoes} {item.qt_apuracoes === 1 ? 'apuração' : 'apurações'}
              </Button>
            </ViewApuracaoModal>
          ) : (
            <Text fontSize="11.5px" color="#94A3B8" fontWeight={600}>
              Sem apurações
            </Text>
          )}
        </Flex>
      </Flex>

      {/* Receita financeira marcada como faturamento no Domínio */}
      {rf && (
        <Flex
          mt={3}
          gap={2}
          align="flex-start"
          p="9px 12px"
          borderRadius="12px"
          bg="#FFFBEB"
          border="1px solid #FDE68A"
          role="note"
        >
          <Box color="#D97706" mt="1px" flex="none">
            <Warning size={16} weight="fill" />
          </Box>
          <Box minW={0}>
            <Text fontSize="12px" fontWeight={700} color="#92400E">
              Receita financeira no faturamento do Domínio · {formatarEmReais(rf.total)}
            </Text>
            <Text fontSize="11.5px" color="#A16207" lineHeight="1.45">
              {textoReceitaFinanceira(rf, cfg.regime)}
            </Text>
          </Box>
        </Flex>
      )}

      {/* Receita financeira já fora do faturamento (cadastro certo): só informa */}
      {item.receitas_financeiras_fora && (
        <Flex
          mt={3}
          gap={2}
          align="flex-start"
          p="9px 12px"
          borderRadius="12px"
          bg="#F0F9FF"
          border="1px solid #BAE6FD"
          role="note"
        >
          <Box color="#0284C7" mt="1px" flex="none">
            <Info size={16} weight="fill" />
          </Box>
          <Box minW={0}>
            <Text fontSize="12px" fontWeight={700} color="#075985">
              Receita financeira no mês · {formatarEmReais(item.receitas_financeiras_fora.total)}
            </Text>
            <Text fontSize="11.5px" color="#0369A1" lineHeight="1.45">
              {textoReceitaFinanceiraFora(item.receitas_financeiras_fora, cfg.regime)}
            </Text>
          </Box>
        </Flex>
      )}

      {/* Motivo provável das divergências com o Domínio */}
      {motivos.length > 0 && (
        <Box
          mt={3}
          p="9px 12px"
          borderRadius="12px"
          bg="#F8FAFC"
          border="1px solid #CBD5E1"
          role="note"
        >
          <Flex align="center" gap={2} mb={1}>
            <Box color="#475569" flex="none">
              <MagnifyingGlass size={15} weight="bold" />
            </Box>
            <Text fontSize="12px" fontWeight={700} color="#334155">
              Por que diverge do Domínio
            </Text>
          </Flex>
          {motivos.map(m => (
            <Box key={m.chave} mt={1} pl="23px">
              <Text fontSize="11.5px" fontWeight={700} color="#334155">
                {m.titulo}
              </Text>
              <Text fontSize="11.5px" color="#475569" lineHeight="1.45">
                {m.texto}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      {/* Faturamento */}
      <SimpleGrid
        mt={3}
        columns={{ base: 2, md: rf ? 6 : 5 }}
        spacing={3}
        p="10px 12px"
        borderRadius="12px"
        bg="rgba(248,250,252,0.8)"
        border="1px solid rgba(15,23,42,0.05)"
      >
        <Metrica
          rotulo="Total faturamento"
          valor={formatarEmReais(calc?.total_faturamento)}
          destaque
          dica={rf?.descontado ? 'Já sem a receita financeira' : undefined}
        />
        <Metrica
          rotulo="Consolidado"
          valor={formatarEmReais(calc?.faturamento_acumulado)}
          dica="Matriz + filiais com a mesma raiz de CNPJ"
        />
        <Metrica rotulo="Saída" valor={formatarEmReais(calc?.vl_saida)} />
        <Metrica rotulo="Serviço" valor={formatarEmReais(calc?.vl_servico)} />
        <Metrica rotulo="Outros" valor={formatarEmReais(calc?.vl_outros)} />
        {rf && (
          <Metrica
            rotulo="Receita financeira"
            valor={`− ${formatarEmReais(rf.descontado)}`}
            dica="Retirada do faturamento desta tela"
          />
        )}
      </SimpleGrid>

      {/* Impostos */}
      <SimpleGrid
        mt={3}
        columns={{ base: 1, sm: 2, md: 3, xl: 5, '2xl': cfg.impostos.length }}
        spacing={2}
      >
        {cfg.impostos.map(imposto => (
          <ImpostoCard key={imposto.key} item={item} imposto={imposto} cfg={cfg} />
        ))}
      </SimpleGrid>

      {/* Ações */}
      <Flex mt={3} justify="flex-end" gap={2} wrap="wrap">
        <Button
          size="sm"
          borderRadius="10px"
          variant={item.paga_por_quotas ? 'solid' : 'outline'}
          colorScheme={item.paga_por_quotas ? 'purple' : 'gray'}
          leftIcon={<Coins size={16} weight="duotone" />}
          isLoading={quotaPendente}
          onClick={onQuota}
        >
          {item.paga_por_quotas ? 'Quota' : 'Marcar Quota'}
        </Button>
        <ViewNotaModal
          screen="fiscal"
          id={item.id}
          dateStart={dateStart}
          dateEnd={dateEnd}
          defaultTab="fiscal"
          companyName={item.razao_social}
          motivos={cfg.motivos}
        >
          <Button
            size="sm"
            borderRadius="10px"
            variant="outline"
            colorScheme="blue"
            leftIcon={<NotePencil size={16} weight="duotone" />}
          >
            Ver anotações
          </Button>
        </ViewNotaModal>
      </Flex>
    </GlassCard>
  );
};
